<!-- Main frame , manage planning -->
<template>
  <div class="planning-content">
    <b-loading :active.sync="isLoading" :can-cancel="false"></b-loading>
    <b-tabs id="tabsPlanning" type="is-boxed" destroy-on-hide v-model="activeTab">
      <b-tab-item v-for="(planning, index) in plannings" :key="index">
        <template slot="header">
          <div align="center">
            <div class="title_planning">{{ planning.name }}</div>
            <div>
              {{ formatDayOfYear(planning.startDayInYear) }}&nbsp;-&nbsp;{{
                formatDayOfYear(planning.endDayInYear)
              }}&nbsp;
              <span>
                <b-tooltip
                  :label="$t('PlanningViewer.tooltip_editplanning')"
                  type="is-secondary"
                  position="is-top"
                >
                  <b-icon
                    class="is-secondary"
                    icon="pencil"
                    @click.native="editPlanning(planning)"
                  />
                </b-tooltip>
                <b-tooltip
                  :label="$t('PlanningViewer.tooltip_exportplanning')"
                  type="is-secondary"
                  position="is-top"
                >
                  <b-icon
                    class="is-secondary"
                    icon="file-excel"
                    @click.native="exportAsXLSPlanning(planning)"
                  />
                </b-tooltip>
              </span>
            </div>
            <div></div>
          </div>
        </template>

        <div class="tab-item-content">
          <div class="columns height-100">
            <div class="route-viewer-container column height-100">
              <RouteViewer :planningId="planning.id"></RouteViewer>
            </div>
          </div>
        </div>
      </b-tab-item>

      <b-tab-item>
        <template slot="header">
          <b-icon icon="calendar-plus"></b-icon>
          <span>{{ $t("PlanningViewer.add_planning_title") }}</span>
        </template>
        <div class="tab-item-content">
          <div class="columns">
            <div class="column is-7 is-offset-5" style="text-align: right">
              <button
                class="button is-primary is-small"
                @click="importPlanning()"
              >
                {{ $t("PlanningViewer.import_planning") }}
              </button>
            </div>
          </div>

          <div class="card" style="height: 100%; overflow: auto">
            <div class="columns is-centered">
              <div class="column is-6">
                <b-field
                  :label="$t('PlanningViewer.input_planningname')"
                  :type="{ 'is-danger': errors.has('name') }"
                  :message="errors.first('name')"
                >
                  <b-input
                    name="name"
                    v-validate="'required'"
                    v-model="form.name"
                    maxlength="20"
                    :placeholder="
                      $t('PlanningViewer.input_planningname_placeholder')
                    "
                  ></b-input>
                </b-field>
              </div>
            </div>
            <div class="columns is-centered">
              <div class="column is-3">
                <b-field
                  :label="$t('PlanningViewer.input_start')"
                  :type="{ 'is-danger': errors.has('start') }"
                  :message="errors.first('start')"
                >
                  <b-datepicker
                    name="start"
                    v-validate="'required'"
                    :placeholder="$t('PlanningViewer.input_start_placeholder')"
                    icon="calendar-today"
                    v-model="form.startDate"
                    :min-date="FIRST_DAY_YEAR"
                    :max-date="maxDate"
                  ></b-datepicker>
                </b-field>
              </div>

              <div class="column is-3">
                <b-field
                  :label="$t('PlanningViewer.input_end')"
                  :type="{ 'is-danger': errors.has('end') }"
                  :message="errors.first('end')"
                >
                  <b-datepicker
                    name="end"
                    v-validate="'required'"
                    :placeholder="$t('PlanningViewer.input_end_placeholder')"
                    icon="calendar-today"
                    v-model="form.endDate"
                    :min-date="minDate"
                    :max-date="LAST_DAY_YEAR"
                  ></b-datepicker>
                </b-field>
              </div>
            </div>

            <div class="columns is-centered">
              <div class="column is-6" style="text-align: center">
                <button class="button is-primary is-small" @click="add()">
                  {{ $t("PlanningViewer.action_add") }}
                </button>
                &nbsp;
              </div>
            </div>
          </div>
        </div>
      </b-tab-item>

      <b-tab-item>
        <template slot="header">
          <b-icon icon="truck-delivery"></b-icon>
          <span>{{ $t("PlanningViewer.title_vehicle") }}</span>
        </template>
        <div class="tab-item-content">
          <div class="columns" style="height: 100%">
            <div class="column is-6" style="height: 100%">
              <div class="card" style="height: 100%; overflow: auto">
                <div class="columns">
                  <div class="column" align="center">
                    <span class="title">{{
                      $t("PlanningViewer.subtitle_left_vehicle")
                    }}</span>
                  </div>
                </div>

                <div class="columns">
                  <div class="column">
                    <b-table
                      @select="selectRow"
                      :data="vehicles"
                      :paginated="true"
                      :per-page="6"
                      :selected.sync="selected"
                      aria-next-label="Next page"
                      aria-previous-label="Previous page"
                      aria-page-label="Page"
                      aria-current-label="Current page"
                    >
                      <template slot-scope="props">
                        <b-table-column
                          field="vehicleRegistration"
                          :label="
                            $t('PlanningViewer.table_vehicle_registration')
                          "
                          width="40"
                          sortable
                        >
                          {{ props.row.vehicleRegistration }}
                        </b-table-column>

                        <b-table-column
                          field="trolleyCapacity"
                          :label="$t('PlanningViewer.table_trolley_capacity')"
                          width="20"
                          sortable
                          numeric
                        >
                          {{ props.row.trolleyCapacity }}
                        </b-table-column>

                        <b-table-column
                          field="maxWeight"
                          :label="$t('PlanningViewer.table_max_weight')"
                          width="20"
                          sortable
                          numeric
                        >
                          {{ props.row.maxWeight }}
                        </b-table-column>

                        <b-table-column
                          field="comments"
                          :label="$t('PlanningViewer.table_comment')"
                          width="60"
                        >
                          {{ props.row.comments }}
                        </b-table-column>
                      </template>
                    </b-table>
                  </div>
                </div>

                <div class="columns">
                  <div class="column" align="center">
                    <button class="button is-danger" @click="deleteVehicle()">
                      {{ $t("PlanningViewer.action_delete") }}</button
                    >&nbsp;
                    <button class="button is-primary" @click="addVehicule()">
                      {{ $t("PlanningViewer.action_add") }}
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div class="column is-6">
              <div class="card" style="height: 100%; overflow: auto">
                <div class="columns">
                  <div class="column" align="center">
                    <span class="title">
                      {{ $t("PlanningViewer.subtitle_right_vehicle") }}
                    </span>
                  </div>
                </div>

                <div class="columns">
                  <div class="column">
                    <b-field
                      :label="$t('PlanningViewer.input_vehicle_registration')"
                      :type="{ 'is-danger': errors.has('vehicleRegistration') }"
                      :message="errors.first('vehicleRegistration')"
                    >
                      <b-input
                        size="is-small"
                        maxlength="30"
                        v-validate="'required'"
                        v-model="vehicle.vehicleRegistration"
                        name="vehicleRegistration"
                        :placeholder="
                          $t('PlanningViewer.input_vehicle_registration')
                        "
                      ></b-input>
                    </b-field>

                    <div class="columns">
                      <div class="column is-6">
                        <b-field
                          :label="$t('PlanningViewer.input_trolley_capacity')"
                        >
                          <b-numberinput
                            min="0"
                            v-model="vehicle.trolleyCapacity"
                            size="is-small"
                            :placeholder="
                              $t('PlanningViewer.input_trolley_capacity')
                            "
                          ></b-numberinput>
                        </b-field>
                      </div>
                      <div class="column is-6">
                        <b-field :label="$t('PlanningViewer.input_max_weight')">
                          <b-numberinput
                            min="0"
                            v-model="vehicle.maxWeight"
                            size="is-small"
                            :placeholder="$t('PlanningViewer.input_max_weight')"
                          ></b-numberinput>
                        </b-field>
                      </div>
                    </div>

                    <b-field :label="$t('PlanningViewer.input_comment')">
                      <b-input
                        size="is-small"
                        v-model="vehicle.comments"
                        type="textarea"
                        maxlength="100"
                        rows="4"
                        :placeholder="$t('PlanningViewer.input_comment')"
                      ></b-input>
                    </b-field>
                  </div>
                </div>

                <div class="columns">
                  <div class="column" align="center">
                    <button class="button is-primary" @click="saveVehicle()">
                      {{ $t("PlanningViewer.action_save") }}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </b-tab-item>
    </b-tabs>
  </div>
</template>

<script>
import RouteViewer from './RouteViewer'
import { mapGetters } from 'vuex'
import moment from 'moment'
import EditionPlanningModal from './PlanningModal.vue'
import ImportPlanningModal from './PlanningImportModal.vue'
import { undoRedoHistory } from '../store/plugins/UndoRedoHistory.js'

export default {
  name: 'PlanningViewer',

  components: {
    RouteViewer
  },

  /* Initialisation of the component, loading data... */
  mounted () {
    this.$log.info('Request all planning...')
    this.$store.dispatch('planning/retrieveAllPlannings')
    this.$log.info('Request all vehicles...')
    this.$store.dispatch('planning/retrieveAllVehicles')
  },

  props: {},

  data () {
    return {
      /** Const */
      LAST_DAY_YEAR: new Date(new Date().getFullYear(), 11, 31),
      FIRST_DAY_YEAR: new Date(new Date().getFullYear(), 0, 1),

      /** Form planning */
      form: {
        name: '',
        startDate: null,
        endDate: null
      },
      /** Form planning */
      importModalActive: false,
      editPlanningModal: false,
      jsonDataImported: '',

      /** Vehicle Part */
      isOpen: false,
      selected: {},
      vehicle: {
        id: -1,
        vehicleRegistration: '',
        trolleyCapacity: 0,
        maxWeight: 0.0,
        comments: ''
      }
    }
  },

  computed: {
    ...mapGetters('planning', ['selectedPlanning', 'plannings', 'isLoading', 'vehicles']),
    minDate: function () {
      return this.form.startDate
    },

    maxDate: function () {
      return this.form.endDate
    },

    activeTab: {
      get () {
        if (this.selectedPlanning) {
          const index = this.plannings.findIndex(p => p.id === this.selectedPlanning.id)
          if (index > 0) {
            return index
          }
        }
        return 0
      },
      set (activeTab) {
        const planning = this.plannings[activeTab]
        this.$store.commit('planning/setSelectedPlanning', planning)
      }
    }
  },

  methods: {
    undo () {
      undoRedoHistory.undo()
    },

    redo () {
      undoRedoHistory.redo()
    },

    editPlanning (planning) {
      this.$buefy.modal.open({
        parent: this,
        component: EditionPlanningModal,
        props: {
          planningId: planning.id,
          planningName: planning.name,
          startDayInYear: planning.startDayInYear,
          endDayInYear: planning.endDayInYear
        }
      })
    },

    importPlanning () {
      this.$buefy.modal.open({
        parent: this,
        component: ImportPlanningModal
      })
    },

    exportAsXLSPlanning (planning) {
      this.$store.dispatch('planning/retrieveExportXlsPlanning', planning)
    },

    /** Add Planning */
    add () {
      this.$validator.validate().then((valid) => {
        if (valid) {
          const planning = {}
          planning.name = this.form.name
          planning.startDayInYear = moment(this.form.startDate).dayOfYear()
          planning.endDayInYear = moment(this.form.endDate).dayOfYear()
          this.$store.dispatch('planning/addPlanning', planning).then(() => {
            this.form.startDate = null
            this.form.endDate = null
          })
        }
      })
    },

    /***
     * Format Day of year as date
     */
    formatDayOfYear (dayOfYear) {
      if (!moment().isLeapYear() && dayOfYear === 366) {
        return moment()
          .dayOfYear(dayOfYear - 1)
          .format('MMM Do')
      }
      return moment().dayOfYear(dayOfYear).format('MMM Do')
    },

    addVehicule: function () {
      this.vehicle = {
        id: -1,
        vehicleRegistration: '',
        trolleyCapacity: 0,
        maxWeight: 0.0,
        comments: ''
      }
      this.selected = null

      this.$validator.reset('vehicleRegistration')
    },

    selectRow: function (row) {
      this.selected = null
      if (row) {
        // Workaround to clone object
        this.vehicle = JSON.parse(JSON.stringify(row))
      }
    },

    saveVehicle: function () {
      if (this.vehicle != null) {
        const vehicleToCopy = JSON.parse(JSON.stringify(this.vehicle))
        this.$validator.validate().then((valid) => {
          if (valid) {
            if (vehicleToCopy.id !== -1) {
              this.$store.dispatch('planning/updateVehicle', vehicleToCopy)
            } else {
              // remove ID
              delete vehicleToCopy.id
              this.$store.dispatch('planning/addVehicle', vehicleToCopy)
            }
            this.selected = null
          }
        })

        this.vehicle = {
          id: -1,
          vehicleRegistration: '',
          trolleyCapacity: 0,
          maxWeight: 0.0,
          comments: ''
        }
      }
    },

    deleteVehicle: function () {
      // Add vehicle
      if (this.vehicle !== -1) {
        this.$store.dispatch('planning/deleteVehicle', this.vehicle.id)
        this.vehicle = {
          id: -1,
          vehicleRegistration: '',
          trolleyCapacity: 0,
          maxWeight: 0.0,
          comments: ''
        }
      }
    }
  }
}
</script>

<style lang="scss">
.tab-item-content {
  display: flex;
  height: 100%;
  flex-direction: column;
}

.route-viewer-container {
  padding: 0;
  margin: 0;
  max-width: 100%;
}

.height-100 {
  height: 100%;
}

.tabs li.is-active a {
  color: #ac936c;
}

.tabs.is-boxed li.is-active a {
  background-color: white;
  border-top-width: 4px;
  border-top-color: #ac936c !important;
  border-bottom-color: transparent !important;
  border-bottom-width: 5px !important;
}

.tabs a {
  color: #448993;
}

.title {
  font-size: 2em;
}

.title_planning {
  font-size: 1.2em;
}

.columns,
.column {
  margin: 0;
}
</style>
